'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { motion } from 'framer-motion';
import { TrendingUp, Calendar, Zap, Sun, Droplets, Leaf } from 'lucide-react';
import HistoricDevelopmentChartMobile from './HistoricDevelopmentChartMobile';

interface HistoricDataPoint {
  year: number;
  installations: number;
  totalPower: number;
  solarInstallations: number;
  waterInstallations: number;
  biomassInstallations: number;
  biogasInstallations: number;
  solarPower: number;
  waterPower: number;
  biomassPower: number;
  biogasPower: number;
  isProjected?: boolean;
  growthRate?: number;
}

interface HistoricDevelopmentChartProps {
  title?: string;
  className?: string;
  showTrendLine?: boolean;
  showProjections?: boolean;
  gemeinde?: string; // PLZ or gemeinde name for cache key consistency
  scenarioData?: {
    increasedSolar: number;
    increasedBiomass: number;
    increasedBiogas: number;
    increasedHydro: number;
    einspeisungRate?: number;
  } | null;
  energyData?: {
    sonne: Array<any>;
    wasser: Array<any>;
    biomasse: Array<any>;
    biogas: Array<any>;
  } | null;
}

/**
 * Calculate energy type specific growth rates from historical data
 * Fixed to work with raw JSON data structure from actual data files
 */
function calculateEnergyTypeGrowthRates(energyData: {
  sonne: Array<any>;
  wasser: Array<any>;
  biomasse: Array<any>;
  biogas: Array<any>;
}): { solar: number; water: number; biomass: number; biogas: number } {
  const calculateGrowthForType = (plants: any[], yearRange: number[] = [2020, 2024]) => {
    if (!plants || plants.length === 0) return 0;
    
    const yearTotals: { [year: number]: number } = {};
    
    // Calculate total energy production per year for this type using raw JSON structure
    plants.forEach(plant => {
      for (let year = yearRange[0]; year <= yearRange[1]; year++) {
        const yearKey = `Eingespeister Strom (kWh) ${year}`;
        const yearData = plant[yearKey];
        
        if (yearData && yearData > 0) {
          yearTotals[year] = (yearTotals[year] || 0) + yearData;
        }
      }
    });
    
    const years = Object.keys(yearTotals).map(Number).sort();
    if (years.length < 2) return 0;
    
    // Calculate compound annual growth rate (CAGR)
    const firstYear = years[0];
    const lastYear = years[years.length - 1];
    const firstValue = yearTotals[firstYear];
    const lastValue = yearTotals[lastYear];
    
    if (firstValue <= 0) return 0;
    
    const yearsSpan = lastYear - firstYear;
    const cagr = Math.pow(lastValue / firstValue, 1 / yearsSpan) - 1;
    
    return cagr * 100; // Convert to percentage
  };
  
  const solarGrowth = calculateGrowthForType(energyData.sonne);
  const waterGrowth = calculateGrowthForType(energyData.wasser);
  const biomassGrowth = calculateGrowthForType(energyData.biomasse);
  const biogasGrowth = calculateGrowthForType(energyData.biogas);
  
  return {
    // Apply realistic constraints based on Austrian energy market
    // These values match the growth factors from energyService for consistency
    solar: Math.min(Math.max(solarGrowth, 8), 15), // PV: 8-15% realistic range, default ~12%
    water: Math.min(Math.max(waterGrowth, 0), 1), // Wasserkraft: 0-1% (nearly saturated), default ~0.5%
    biomass: Math.min(Math.max(biomassGrowth, 0), 2), // Biomasse: 0-2% (resource limited), default ~1%
    biogas: Math.min(Math.max(biogasGrowth, 0), 2)  // Biogas: 0-2% (substrate limited), default ~1%
  };
}

/**
 * Extract real 2025 data from JSON files if available
 * Includes data quality validation to prevent unrealistic projections
 */
function extractReal2025Data(
  sonneData: any[], 
  wasserData: any[], 
  biomasseData: any[], 
  biogasData: any[],
  reference2024Data?: {
    solarPower: number;
    waterPower: number;
    biomassPower: number;
    biogasPower: number;
    solarInstallations: number;
    waterInstallations: number;
    biomassInstallations: number;
    biogasInstallations: number;
  }
): {
  hasData: boolean;
  solarInstallations: number;
  waterInstallations: number;
  biomassInstallations: number;
  biogasInstallations: number;
  solarPower: number;
  waterPower: number;
  biomassPower: number;
  biogasPower: number;
  isRealistic: boolean;
  qualityIssues: string[];
} {
  let solarInstallations = 0;
  let waterInstallations = 0;
  let biomassInstallations = 0;
  let biogasInstallations = 0;
  
  let solarPower = 0;
  let waterPower = 0;
  let biomassPower = 0;
  let biogasPower = 0;
  
  const qualityIssues: string[] = [];
  
  // Use consistent counting method - same as getInstallationsWithPowerInYear
  solarInstallations = getInstallationsWithPowerInYear(sonneData, 2025);
  waterInstallations = getInstallationsWithPowerInYear(wasserData, 2025);
  biomassInstallations = getInstallationsWithPowerInYear(biomasseData, 2025);
  biogasInstallations = getInstallationsWithPowerInYear(biogasData, 2025);
  
  // Calculate power totals for 2025 using same logic as main calculation
  const calculatePowerFor2025 = (installations: any[]) => {
    return installations.reduce((total, installation) => {
      // Try historischeErträge structure first
      if (installation.historischeErträge && Array.isArray(installation.historischeErträge)) {
        const yearData = installation.historischeErträge.find((ertrag: any) => ertrag.jahr === 2025);
        if (yearData && yearData.ertragKwh > 0) {
          return total + yearData.ertragKwh;
        }
      }
      
      // Fallback to raw JSON structure
      const power2025 = installation['Eingespeister Strom (kWh) 2025'];
      if (power2025 && power2025 > 0) {
        return total + power2025;
      }
      
      return total;
    }, 0);
  };
  
  solarPower = calculatePowerFor2025(sonneData);
  waterPower = calculatePowerFor2025(wasserData);
  biomassPower = calculatePowerFor2025(biomasseData);
  biogasPower = calculatePowerFor2025(biogasData);
  
  const totalInstallations = solarInstallations + waterInstallations + biomassInstallations + biogasInstallations;
  const totalPower = solarPower + waterPower + biomassPower + biogasPower;
  
  // Data Quality Validation against 2024 reference data
  let isRealistic = true;
  
  if (reference2024Data) {
    // Check for unrealistic decreases in power (more than 50% drop is suspicious)
    const solarDecrease = reference2024Data.solarPower > 0 ? ((reference2024Data.solarPower - solarPower) / reference2024Data.solarPower) * 100 : 0;
    const waterDecrease = reference2024Data.waterPower > 0 ? ((reference2024Data.waterPower - waterPower) / reference2024Data.waterPower) * 100 : 0;
    const totalDecrease = (reference2024Data.solarPower + reference2024Data.waterPower + reference2024Data.biomassPower + reference2024Data.biogasPower) > 0 
      ? (((reference2024Data.solarPower + reference2024Data.waterPower + reference2024Data.biomassPower + reference2024Data.biogasPower) - totalPower) / (reference2024Data.solarPower + reference2024Data.waterPower + reference2024Data.biomassPower + reference2024Data.biogasPower)) * 100 
      : 0;
    
    if (solarDecrease > 50) {
      qualityIssues.push(`Solar power decreased by ${solarDecrease.toFixed(1)}% (${reference2024Data.solarPower} → ${solarPower})`);
      isRealistic = false;
    }
    
    if (waterDecrease > 50) {
      qualityIssues.push(`Water power decreased by ${waterDecrease.toFixed(1)}% (${reference2024Data.waterPower} → ${waterPower})`);
      isRealistic = false;
    }
    
    if (totalDecrease > 30) {
      qualityIssues.push(`Total power decreased by ${totalDecrease.toFixed(1)}% - unrealistic for renewable energy growth`);
      isRealistic = false;
    }
    
    // Check for installation decrease (installations should generally not decrease)
    if (solarInstallations < reference2024Data.solarInstallations) {
      qualityIssues.push(`Solar installations decreased from ${reference2024Data.solarInstallations} to ${solarInstallations}`);
      isRealistic = false;
    }
    
    // Check for installation vs power mismatch (more installations but much less power)
    if (solarInstallations > reference2024Data.solarInstallations && solarPower < reference2024Data.solarPower * 0.8) {
      qualityIssues.push(`More solar installations (${solarInstallations} vs ${reference2024Data.solarInstallations}) but 20%+ less power`);
      isRealistic = false;
    }
  }
  
  console.log('[extractReal2025Data] Quality Analysis:', {
    totalInstallations,
    totalPower,
    solarInstallations,
    solarPower,
    waterInstallations,
    waterPower,
    isRealistic,
    qualityIssues,
    reference2024: reference2024Data
  });
  
  return {
    hasData: totalInstallations > 0 && totalPower > 0,
    solarInstallations,
    waterInstallations,
    biomassInstallations,
    biogasInstallations,
    solarPower: Math.round(solarPower),
    waterPower: Math.round(waterPower),
    biomassPower: Math.round(biomassPower),
    biogasPower: Math.round(biogasPower),
    isRealistic,
    qualityIssues
  };
}

/**
 * Calculate realistic growth rates based on historical trend (not artificial amplification)
 */
function calculateRealisticGrowthRates(historicData: HistoricDataPoint[]): {
  solar: number;
  water: number;
  biomass: number;
  biogas: number;
} {
  if (historicData.length < 3) {
    // Fallback to conservative defaults if insufficient data
    return { solar: 8, water: 0.5, biomass: 1, biogas: 1 };
  }
  
  // Use last 3 years for trend calculation to avoid outliers
  const recentYears = historicData.slice(-3);
  
  // Calculate year-over-year growth rates for each energy type
  const calculateTrendGrowth = (getValue: (point: HistoricDataPoint) => number) => {
    const growthRates: number[] = [];
    
    for (let i = 1; i < recentYears.length; i++) {
      const current = getValue(recentYears[i]);
      const previous = getValue(recentYears[i - 1]);
      
      if (previous > 0) {
        const growthRate = ((current - previous) / previous) * 100;
        growthRates.push(growthRate);
      }
    }
    
    if (growthRates.length === 0) return 0;
    
    // Calculate average growth rate
    const avgGrowth = growthRates.reduce((sum, rate) => sum + rate, 0) / growthRates.length;
    return avgGrowth;
  };
  
  const solarGrowth = calculateTrendGrowth(d => d.solarPower);
  const waterGrowth = calculateTrendGrowth(d => d.waterPower);
  const biomassGrowth = calculateTrendGrowth(d => d.biomassPower);
  const biogasGrowth = calculateTrendGrowth(d => d.biogasPower);
  
  // Hybrid approach: PV uses trend-based calculation, others use conservative fixed rates
  
  // Calculate dynamic solar growth based on historical trend
  let solarGrowthRate = 8; // Default fallback
  if (solarGrowth > 0) {
    // Apply realistic bounds for PV trend: 3-20%
    solarGrowthRate = Math.max(3, Math.min(solarGrowth, 20));
  } else if (solarGrowth === 0) {
    // No growth means use conservative rate
    solarGrowthRate = 5;
  }
  // If negative growth, use minimum 3%
  else {
    solarGrowthRate = 3;
  }
  
  return {
    // PV: Dynamic trend-based growth (3-20% range)
    solar: solarGrowthRate,
    // Wasserkraft: Fixed conservative (infrastructure mostly built out)
    water: 0.5,
    // Biomasse: Fixed conservative (resource limited)
    biomass: 1,
    // Biogas: Fixed conservative (substrate limited)
    biogas: 1
  };
}

/**
 * Count installations with power > 0 in a specific year
 * Works with transformed EnergyPlant objects that have historischeErträge structure
 */
function getInstallationsWithPowerInYear(installations: any[], year: number): number {
  let countWithHistorische = 0;
  let countWithRaw = 0;
  let totalInstallations = installations.length;
  
  const count = installations.filter(installation => {
    // Check if this installation has historischeErträge data
    if (installation.historischeErträge && Array.isArray(installation.historischeErträge)) {
      // Find the year data in historischeErträge array
      const yearData = installation.historischeErträge.find((ertrag: any) => ertrag.jahr === year);
      if (yearData && yearData.ertragKwh > 0) {
        countWithHistorische++;
        return true;
      }
    }
    
    // Fallback: Check for raw JSON structure (backwards compatibility)
    const yearKey = `Eingespeister Strom (kWh) ${year}`;
    const rawYearData = installation[yearKey];
    if (rawYearData && rawYearData > 0) {
      countWithRaw++;
      return true;
    }
    
    return false;
  }).length;
  
  // Enhanced debug logging for key years
  if ((year === 2023 || year === 2025) && installations.length > 0) {
    console.log(`[ENHANCED DEBUG] getInstallationsWithPowerInYear for ${year}:`, {
      year,
      totalInstallations,
      countWithHistorische,
      countWithRaw,
      finalCount: count,
      percentage: totalInstallations > 0 ? ((count / totalInstallations) * 100).toFixed(1) + '%' : '0%'
    });
    
    // Show detailed sample data structure for debugging
    const sampleInstallation = installations[0];
    console.log(`[SAMPLE DATA DETAILED] First installation for year ${year}:`, {
      hasHistorischeErtraege: !!sampleInstallation?.historischeErträge,
      historischeErtraegeLength: sampleInstallation?.historischeErträge?.length || 0,
      historischeErtraegeData: sampleInstallation?.historischeErträge || [],
      sampleYearData: sampleInstallation?.historischeErträge?.find((e: any) => e.jahr === year),
      hasRawYearKey: !!sampleInstallation?.[`Eingespeister Strom (kWh) ${year}`],
      rawYearValue: sampleInstallation?.[`Eingespeister Strom (kWh) ${year}`],
      allKeys: Object.keys(sampleInstallation || {}).filter(k => k.includes('201') || k.includes('202') || k.includes('historische'))
    });
    
    // Show multiple sample installations
    if (installations.length > 3) {
      console.log(`[MULTIPLE SAMPLES] ${year} data for first 3 installations:`, 
        installations.slice(0, 3).map((inst, i) => ({
          index: i,
          hasHistorische: !!inst.historischeErträge,
          historischeCount: inst.historischeErträge?.length || 0,
          yearDataInHistorische: inst.historischeErträge?.find((e: any) => e.jahr === year),
          hasRawKey: !!inst[`Eingespeister Strom (kWh) ${year}`],
          rawValue: inst[`Eingespeister Strom (kWh) ${year}`]
        }))
      );
    }
  }
  
  return count;
}

/**
 * Generate historic energy development data from real gemeinde data
 * Fixed to work with raw JSON data structure from actual data files
 */
function generateHistoricDataFromReal(energyData: {
  sonne: Array<any>;
  wasser: Array<any>;
  biomasse: Array<any>;
  biogas: Array<any>;
}, scenarioData?: {
  increasedSolar: number;
  increasedBiomass: number;
  increasedBiogas: number;
  increasedHydro: number;
  einspeisungRate?: number;
} | null, gemeinde?: string | null): HistoricDataPoint[] {
  // Only process historical years, 2025 will be added as projection
  const years = [2020, 2021, 2022, 2023, 2024];
  
  // Safely handle potentially undefined energy data arrays
  const sonneData = energyData.sonne || [];
  const wasserData = energyData.wasser || [];
  const biomasseData = energyData.biomasse || [];
  const biogasData = energyData.biogas || [];
  
  console.log('[HistoricDevelopmentChart] ✅ HYBRID GROWTH v2 - PV Dynamic, Others Conservative ✅:', {
    version: 'HYBRID_GROWTH_RATES_v1',
    sonneCount: sonneData.length,
    wasserCount: wasserData.length,
    biomasseCount: biomasseData.length,
    biogasCount: biogasData.length,
    sampleSolarInstallation: sonneData[0] ? {
      // Raw JSON keys we expect:
      rawId: sonneData[0].ID,
      rawYear2024: sonneData[0]['Eingespeister Strom (kWh) 2024'],
      rawYear2023: sonneData[0]['Eingespeister Strom (kWh) 2023'],
      // Transformed keys that might exist:
      transformedId: sonneData[0].id,
      transformedName: sonneData[0].name,
      transformedType: sonneData[0].type,
      transformedYearlyProduction: sonneData[0].yearlyProduction,
      historischeErtraege: sonneData[0].historischeErträge,
      // Show all keys to debug:
      allKeys: Object.keys(sonneData[0])
    } : null
  });
  
  const historicData = years.map(year => {
    // Calculate installation counts with power > 0 in this specific year
    const solarInstallations = getInstallationsWithPowerInYear(sonneData, year);
    const waterInstallations = getInstallationsWithPowerInYear(wasserData, year);
    const biomassInstallations = getInstallationsWithPowerInYear(biomasseData, year);
    const biogasInstallations = getInstallationsWithPowerInYear(biogasData, year);
    const activeInstallations = solarInstallations + waterInstallations + biomassInstallations + biogasInstallations;
    
    let solarPower = 0;
    let waterPower = 0;
    let biomassPower = 0;
    let biogasPower = 0;
    
    // Calculate energy production by type for this year (in kWh)
    // Works with both transformed EnergyPlant objects and raw JSON
    const calculatePowerForYear = (installations: any[]) => {
      return installations.reduce((total, installation) => {
        // Try historischeErträge structure first
        if (installation.historischeErträge && Array.isArray(installation.historischeErträge)) {
          const yearData = installation.historischeErträge.find((ertrag: any) => ertrag.jahr === year);
          if (yearData && yearData.ertragKwh > 0) {
            return total + yearData.ertragKwh;
          }
        }
        
        // Fallback to raw JSON structure
        const yearKey = `Eingespeister Strom (kWh) ${year}`;
        const rawYearData = installation[yearKey];
        if (rawYearData && rawYearData > 0) {
          return total + rawYearData;
        }
        
        return total;
      }, 0);
    };
    
    solarPower = calculatePowerForYear(sonneData);
    waterPower = calculatePowerForYear(wasserData);
    biomassPower = calculatePowerForYear(biomasseData);
    biogasPower = calculatePowerForYear(biogasData);
    
    const totalPower = solarPower + waterPower + biomassPower + biogasPower;
    
    // Add debug logging for key years
    if ((year === 2023 || year === 2024) && sonneData.length > 100) {
      console.log(`[HistoricDevelopmentChart] Year ${year} installation count validation:`, {
        year,
        solarInstallations,
        waterInstallations,
        biomassInstallations,
        biogasInstallations,
        activeInstallations,
        solarPower,
        totalPower,
        // Show sample installations with power in this year
        sampleSolarWithPower: sonneData.filter(inst => {
          const power = inst[`Eingespeister Strom (kWh) ${year}`];
          return power && power > 0;
        }).slice(0, 3).map(inst => ({
          id: inst.ID,
          powerThisYear: inst[`Eingespeister Strom (kWh) ${year}`]
        }))
      });
    }
    
    return {
      year,
      installations: activeInstallations,
      totalPower: Math.round(totalPower), // Round to whole kWh
      solarInstallations,
      waterInstallations,
      biomassInstallations,
      biogasInstallations,
      solarPower: Math.round(solarPower), // Round to whole kWh
      waterPower: Math.round(waterPower),
      biomassPower: Math.round(biomassPower),
      biogasPower: Math.round(biogasPower),
      isProjected: false
    };
  });
  
  // Calculate growth rates based on energy production (not installation count)
  const dataWithGrowth = historicData.map((point, index) => {
    let growthRate = 0;
    
    if (index > 0) {
      const currentPower = point.totalPower;
      const previousPower = historicData[index - 1].totalPower;
      
      if (previousPower > 0) {
        growthRate = ((currentPower - previousPower) / previousPower) * 100;
      }
    }
    
    return {
      ...point,
      growthRate: Math.round(growthRate * 10) / 10
    };
  });

  // Check localStorage for cached 2025 projection using consistent PLZ-based cache key
  const lastYear = dataWithGrowth[dataWithGrowth.length - 1];
  const cacheKey = gemeinde ? `projection_2025_${gemeinde}` : `projection_2025_${JSON.stringify(lastYear).slice(0, 50)}`;
  
  let projectedSolarInstallations, projectedWaterInstallations, projectedBiomassInstallations, projectedBiogasInstallations;
  let projectedSolarPower, projectedWaterPower, projectedBiomassPower, projectedBiogasPower;
  let realisticGrowthRates;
  
  // Try to get cached 2025 projection
  let cached2025Data = null;
  try {
    const cachedStr = localStorage.getItem(cacheKey);
    if (cachedStr) {
      cached2025Data = JSON.parse(cachedStr);
      console.log('[HistoricDevelopmentChart] Using cached 2025 projection');
    }
  } catch (e) {
    console.warn('[HistoricDevelopmentChart] Could not read cached 2025 data');
  }
  
  if (cached2025Data) {
    // Use cached values
    projectedSolarInstallations = cached2025Data.solarInstallations;
    projectedWaterInstallations = cached2025Data.waterInstallations;
    projectedBiomassInstallations = cached2025Data.biomassInstallations;
    projectedBiogasInstallations = cached2025Data.biogasInstallations;
    projectedSolarPower = cached2025Data.solarPower;
    projectedWaterPower = cached2025Data.waterPower;
    projectedBiomassPower = cached2025Data.biomassPower;
    projectedBiogasPower = cached2025Data.biogasPower;
    realisticGrowthRates = cached2025Data.growthRates;
  } else {
    // Calculate and cache 2025 projection
    realisticGrowthRates = calculateRealisticGrowthRates(dataWithGrowth);
    
    projectedSolarInstallations = lastYear.solarInstallations * (1 + realisticGrowthRates.solar / 100);
    projectedWaterInstallations = lastYear.waterInstallations * (1 + realisticGrowthRates.water / 100);
    projectedBiomassInstallations = lastYear.biomassInstallations * (1 + realisticGrowthRates.biomass / 100);
    projectedBiogasInstallations = lastYear.biogasInstallations * (1 + realisticGrowthRates.biogas / 100);
    
    projectedSolarPower = lastYear.solarPower * (1 + realisticGrowthRates.solar / 100);
    projectedWaterPower = lastYear.waterPower * (1 + realisticGrowthRates.water / 100);
    projectedBiomassPower = lastYear.biomassPower * (1 + realisticGrowthRates.biomass / 100);
    projectedBiogasPower = lastYear.biogasPower * (1 + realisticGrowthRates.biogas / 100);
    
    // Cache the calculated values
    try {
      const dataToCache = {
        solarInstallations: projectedSolarInstallations,
        waterInstallations: projectedWaterInstallations,
        biomassInstallations: projectedBiomassInstallations,
        biogasInstallations: projectedBiogasInstallations,
        solarPower: projectedSolarPower,
        waterPower: projectedWaterPower,
        biomassPower: projectedBiomassPower,
        biogasPower: projectedBiogasPower,
        growthRates: realisticGrowthRates,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      console.log('[HistoricDevelopmentChart] Cached 2025 projection with growth rates:', realisticGrowthRates);
    } catch (e) {
      console.warn('[HistoricDevelopmentChart] Could not cache 2025 data');
    }
  }
  
  // Calculate overall projections
  let projectedInstallations = projectedSolarInstallations + projectedWaterInstallations + projectedBiomassInstallations + projectedBiogasInstallations;
  let projectedPower = projectedSolarPower + projectedWaterPower + projectedBiomassPower + projectedBiogasPower;
  
  if (scenarioData) {
    // Apply scenario adjustments directly for live preview
    // Use the exact scenario percentages for immediate feedback
    const solarMultiplier = scenarioData.increasedSolar / 100;
    const hydroMultiplier = (scenarioData.increasedHydro || 0) / 100;  
    const biomassMultiplier = scenarioData.increasedBiomass / 100;
    const biogasMultiplier = scenarioData.increasedBiogas / 100;
    
    // Apply Einspeisung rate adjustment if specified
    const einspeisungMultiplier = (scenarioData.einspeisungRate || 40) / 40;
    
    // Override with scenario values for live chart update
    projectedSolarPower = lastYear.solarPower * (1 + solarMultiplier) * einspeisungMultiplier;
    projectedWaterPower = lastYear.waterPower * (1 + hydroMultiplier) * einspeisungMultiplier;
    projectedBiomassPower = lastYear.biomassPower * (1 + biomassMultiplier) * einspeisungMultiplier;
    projectedBiogasPower = lastYear.biogasPower * (1 + biogasMultiplier) * einspeisungMultiplier;
    
    // Also adjust installations proportionally
    projectedSolarInstallations = lastYear.solarInstallations * (1 + solarMultiplier * 0.5);
    projectedWaterInstallations = lastYear.waterInstallations * (1 + hydroMultiplier * 0.3);
    projectedBiomassInstallations = lastYear.biomassInstallations * (1 + biomassMultiplier * 0.4);
    projectedBiogasInstallations = lastYear.biogasInstallations * (1 + biogasMultiplier * 0.4);
    
    console.log('[HistoricDevelopmentChart] Applied live scenario preview:', {
      solar: `${scenarioData.increasedSolar}% -> ${Math.round(projectedSolarPower)} kWh`,
      water: `${scenarioData.increasedHydro || 0}% -> ${Math.round(projectedWaterPower)} kWh`,
      biomass: `${scenarioData.increasedBiomass}% -> ${Math.round(projectedBiomassPower)} kWh`,
      biogas: `${scenarioData.increasedBiogas}% -> ${Math.round(projectedBiogasPower)} kWh`,
      einspeisungRate: `${scenarioData.einspeisungRate || 40}%`
    });
  }

  // Calculate final projected totals
  const finalInstallations = projectedSolarInstallations + projectedWaterInstallations + projectedBiomassInstallations + projectedBiogasInstallations;
  const finalPower = projectedSolarPower + projectedWaterPower + projectedBiomassPower + projectedBiogasPower;
  
  // Validation: Ensure consistency between installations and power
  if (finalInstallations > lastYear.installations && finalPower < lastYear.totalPower) {
    console.warn('[HistoricDevelopmentChart] Consistency issue detected: More installations but less power. Adjusting projection.', {
      installations: { last: lastYear.installations, projected: finalInstallations },
      power: { last: lastYear.totalPower, projected: finalPower }
    });
    
    // If we have more installations but less power, ensure minimum power increase
    const minPowerIncrease = lastYear.totalPower * 1.005; // At least 0.5% increase
    if (finalPower < minPowerIncrease) {
      const powerAdjustment = minPowerIncrease / finalPower;
      projectedSolarPower *= powerAdjustment;
      projectedWaterPower *= powerAdjustment;
      projectedBiomassPower *= powerAdjustment;
      projectedBiogasPower *= powerAdjustment;
      
      console.log('[HistoricDevelopmentChart] Applied power adjustment factor:', powerAdjustment.toFixed(3));
    }
  }
  
  // Recalculate final totals after potential adjustments
  const adjustedFinalPower = projectedSolarPower + projectedWaterPower + projectedBiomassPower + projectedBiogasPower;
  
  // Calculate realistic growth rate for display based on the actual projection method used
  const totalGrowthRate = lastYear.totalPower > 0 
    ? ((adjustedFinalPower - lastYear.totalPower) / lastYear.totalPower) * 100
    : 5; // Default 5% growth if no base data
  
  // Ensure minimum realistic growth for renewable energy sector (typically 2-3% annually)
  const displayGrowthRate = Math.max(2.0, totalGrowthRate);
  
  const projection2025: HistoricDataPoint = {
    year: 2025,
    installations: Math.round(finalInstallations),
    totalPower: Math.round(adjustedFinalPower),
    solarInstallations: Math.round(projectedSolarInstallations),
    waterInstallations: Math.round(projectedWaterInstallations),
    biomassInstallations: Math.round(projectedBiomassInstallations),
    biogasInstallations: Math.round(projectedBiogasInstallations),
    solarPower: Math.round(projectedSolarPower),
    waterPower: Math.round(projectedWaterPower),
    biomassPower: Math.round(projectedBiomassPower),
    biogasPower: Math.round(projectedBiogasPower),
    isProjected: true, // Always mark as projection since 2025 JSON data is incomplete
    growthRate: Math.round(displayGrowthRate * 10) / 10
  };
  
  console.log('[HistoricDevelopmentChart] 2025 Data calculation:', {
    dataSource: cached2025Data ? 'localStorage cache' : 'Fresh calculation',
    lastYear2024: {
      installations: lastYear.installations,
      totalPower: lastYear.totalPower,
      solarInstallations: lastYear.solarInstallations,
      solarPower: lastYear.solarPower
    },
    finalValues: {
      installations: finalInstallations,
      totalPower: adjustedFinalPower,
      solarInstallations: projectedSolarInstallations,
      solarPower: projectedSolarPower
    },
    growthRate: displayGrowthRate.toFixed(1) + '%',
    final2025: projection2025
  });

  // If no historical data found, create fallback data based on current installations
  if (dataWithGrowth.every(point => point.totalPower === 0)) {
    console.warn('[HistoricDevelopmentChart] No historical data found, using current data as fallback');
    
    // Use current energy data from 2024 to create a simple trend
    const currentSolar = sonneData.reduce((sum, plant) => {
      const current2024 = plant['Eingespeister Strom (kWh) 2024'] || 0;
      return sum + current2024;
    }, 0); // Keep in kWh
    const currentWater = wasserData.reduce((sum, plant) => {
      const current2024 = plant['Eingespeister Strom (kWh) 2024'] || 0;
      return sum + current2024;
    }, 0);
    const currentBiomass = biomasseData.reduce((sum, plant) => {
      const current2024 = plant['Eingespeister Strom (kWh) 2024'] || 0;
      return sum + current2024;
    }, 0);
    const currentBiogas = biogasData.reduce((sum, plant) => {
      const current2024 = plant['Eingespeister Strom (kWh) 2024'] || 0;
      return sum + current2024;
    }, 0);
    
    console.warn('[HistoricDevelopmentChart] Current energy values (kWh):', {
      currentSolar,
      currentWater,
      currentBiomass,
      currentBiogas,
      total: currentSolar + currentWater + currentBiomass + currentBiogas
    });
    
    // If current values are also 0, use minimal fallback data
    const hasCurrentData = (currentSolar + currentWater + currentBiomass + currentBiogas) > 0;
    
    const fallbackData = years.map((year, index) => {
      const factor = Math.pow(1.05, index); // 5% annual growth as fallback
      
      // Use minimal values if no current data available
      const baseSolar = hasCurrentData ? currentSolar : 100000; // 100,000 kWh minimum
      const baseWater = hasCurrentData ? currentWater : 50000;
      const baseBiomass = hasCurrentData ? currentBiomass : 20000;
      const baseBiogas = hasCurrentData ? currentBiogas : 20000;
      
      return {
        year,
        installations: Math.max(sonneData.length + wasserData.length + biomasseData.length + biogasData.length, 1),
        totalPower: Math.round((baseSolar + baseWater + baseBiomass + baseBiogas) * factor),
        solarInstallations: Math.max(sonneData.length, hasCurrentData ? sonneData.length : 1),
        waterInstallations: wasserData.length,
        biomassInstallations: biomasseData.length,
        biogasInstallations: biogasData.length,
        solarPower: Math.round(baseSolar * factor),
        waterPower: Math.round(baseWater * factor),
        biomassPower: Math.round(baseBiomass * factor),
        biogasPower: Math.round(baseBiogas * factor),
        isProjected: false,
        growthRate: index > 0 ? 5 : 0
      };
    });
    
    // Ensure 2025 projection has meaningful values
    const enhanced2025 = {
      ...projection2025,
      totalPower: Math.max(projection2025.totalPower, 500000), // Minimum 500,000 kWh
      solarPower: Math.max(projection2025.solarPower, hasCurrentData ? projection2025.solarPower : 300000),
      waterPower: Math.max(projection2025.waterPower, 100000),
      biomassPower: Math.max(projection2025.biomassPower, 50000),
      biogasPower: Math.max(projection2025.biogasPower, 50000)
    };
    
    // Add 2025 projection with enhanced values
    fallbackData.push(enhanced2025);
    
    console.warn('[HistoricDevelopmentChart] Generated fallback data:', {
      dataLength: fallbackData.length,
      samplePoint: fallbackData[2],
      projection: enhanced2025
    });
    
    return fallbackData;
  }

  return [...dataWithGrowth, projection2025];
}

/**
 * Generate mock historic energy development data (fallback)
 * Based on Austria's renewable energy expansion trends
 */
function generateMockHistoricData(scenarioData?: {
  increasedSolar: number;
  increasedBiomass: number;
  increasedBiogas: number;
  increasedHydro: number;
  einspeisungRate?: number;
} | null): HistoricDataPoint[] {
  // Base data roughly matching Austria's renewable energy growth
  const baseData = [
    { year: 2020, installations: 28000, totalPower: 3200, solarInstallations: 22000, waterInstallations: 4500, biomassInstallations: 1200, biogasInstallations: 300, solarPower: 2100, waterPower: 900, biomassPower: 150, biogasPower: 50 },
    { year: 2021, installations: 30500, totalPower: 3580, solarInstallations: 24200, waterInstallations: 4700, biomassInstallations: 1300, biogasInstallations: 300, solarPower: 2350, waterPower: 1000, biomassPower: 180, biogasPower: 50 },
    { year: 2022, installations: 33200, totalPower: 4020, solarInstallations: 26800, waterInstallations: 4800, biomassInstallations: 1350, biogasInstallations: 250, solarPower: 2700, waterPower: 1100, biomassPower: 170, biogasPower: 50 },
    { year: 2023, installations: 35100, totalPower: 4350, solarInstallations: 28900, waterInstallations: 4900, biomassInstallations: 1050, biogasInstallations: 250, solarPower: 3000, waterPower: 1150, biomassPower: 150, biogasPower: 50 },
    { year: 2024, installations: 35800, totalPower: 4520, solarInstallations: 29500, waterInstallations: 5000, biomassInstallations: 1050, biogasInstallations: 250, solarPower: 3150, waterPower: 1200, biomassPower: 120, biogasPower: 50 }
  ];

  // Calculate growth rates and add projections
  const dataWithGrowth = baseData.map((point, index) => {
    const growthRate = index > 0 
      ? ((point.installations - baseData[index - 1].installations) / baseData[index - 1].installations) * 100
      : 0;
    
    return {
      ...point,
      isProjected: false,
      growthRate: Math.round(growthRate * 10) / 10
    };
  });

  // Add 2025 projection based on trend and scenario
  const avgGrowthRate = dataWithGrowth
    .slice(1)
    .reduce((sum, point) => sum + (point.growthRate || 0), 0) / 4;

  const lastYear = dataWithGrowth[dataWithGrowth.length - 1];
  
  // Apply scenario adjustments to 2025 projection
  let projectedInstallations = lastYear.installations * (1 + avgGrowthRate / 100);
  let projectedPower = lastYear.totalPower * (1 + avgGrowthRate / 100);
  
  if (scenarioData) {
    // Calculate additional installations based on scenario
    const additionalSolar = (scenarioData.increasedSolar / 100) * 0.3; // 30% of increase translates to new installations
    const additionalHydro = (scenarioData.increasedHydro / 100) * 0.1; // 10% of increase (fewer but larger installations)
    const additionalBiomass = (scenarioData.increasedBiomass / 100) * 0.05; // 5% (typically larger installations)
    const additionalBiogas = (scenarioData.increasedBiogas / 100) * 0.05; // 5% (typically larger installations)
    
    const totalAdditionalGrowth = additionalSolar + additionalHydro + additionalBiomass + additionalBiogas;
    
    // Apply additional growth to installations and power
    projectedInstallations *= (1 + totalAdditionalGrowth);
    projectedPower *= (1 + totalAdditionalGrowth * 1.2); // Power grows slightly faster than installation count
    
    // Factor in Einspeisung rate improvement (affects efficiency, not necessarily installations)
    if (scenarioData.einspeisungRate && scenarioData.einspeisungRate > 40) {
      const efficiencyBoost = (scenarioData.einspeisungRate - 40) / 40 * 0.1; // 10% boost for 70% Einspeisung
      projectedPower *= (1 + efficiencyBoost);
    }
  }

  const projection2025: HistoricDataPoint = {
    year: 2025,
    installations: Math.round(projectedInstallations),
    totalPower: Math.round(projectedPower),
    solarInstallations: 0,
    waterInstallations: 0,
    biomassInstallations: 0,
    biogasInstallations: 0,
    solarPower: 0,
    waterPower: 0,
    biomassPower: 0,
    biogasPower: 0,
    isProjected: true,
    growthRate: scenarioData ? avgGrowthRate * 1.5 : avgGrowthRate // Higher growth rate with scenario
  };

  return [...dataWithGrowth, projection2025];
}

/**
 * Custom tooltip for the chart
 */
interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: HistoricDataPoint;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isProjected = data.isProjected;

    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-blue-500" />
          <span className="font-semibold">{label}</span>
          {isProjected && (
            <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-2 py-1 rounded">
              Prognose
            </span>
          )}
        </div>
        
        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600 dark:text-gray-400">Anlagen gesamt:</span>
            <span className="font-medium">{data.installations.toLocaleString()}</span>
          </div>
          
          {/* Detailed breakdown by energy type */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-2">
            {data.solarInstallations > 0 && (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-yellow-600 dark:text-yellow-400">☀ Solar:</span>
                <span>{data.solarInstallations.toLocaleString()} Anlagen ({data.solarPower.toLocaleString()} kWh)</span>
              </div>
            )}
            {data.waterInstallations > 0 && (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-blue-600 dark:text-blue-400">💧 Wasser:</span>
                <span>{data.waterInstallations.toLocaleString()} Anlagen ({data.waterPower.toLocaleString()} kWh)</span>
              </div>
            )}
            {data.biomassInstallations > 0 && (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-green-600 dark:text-green-400">🌿 Biomasse:</span>
                <span>{data.biomassInstallations.toLocaleString()} Anlagen ({data.biomassPower.toLocaleString()} kWh)</span>
              </div>
            )}
            {data.biogasInstallations > 0 && (
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-pink-600 dark:text-pink-400">🔥 Biogas:</span>
                <span>{data.biogasInstallations.toLocaleString()} Anlagen ({data.biogasPower.toLocaleString()} kWh)</span>
              </div>
            )}
          </div>
          
          <div className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-600 dark:text-gray-400">Einspeisung gesamt:</span>
              <span className="font-medium">{data.totalPower.toLocaleString()} kWh</span>
            </div>
          </div>
          
          {data.growthRate !== undefined && data.growthRate !== 0 && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-gray-600 dark:text-gray-400">Wachstum:</span>
              <span className={`font-medium ${data.growthRate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.growthRate > 0 ? '+' : ''}{data.growthRate.toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

/**
 * Historic Development Chart Component
 * Shows renewable energy growth from 2020-2025 with trend predictions
 */
export const HistoricDevelopmentChart: React.FC<HistoricDevelopmentChartProps> = ({
  title = "Entwicklung Erneuerbarer Energien",
  className = "",
  // showTrendLine = true,
  // showProjections = true
  scenarioData = null,
  energyData = null,
  gemeinde = null
}) => {
  // [DEBUG] Always log when component is called
  console.log('[HistoricDevelopmentChart] 🎯 COMPONENT CALLED WITH:', {
    hasEnergyData: !!energyData,
    energyDataKeys: energyData ? Object.keys(energyData) : null,
    title,
    className
  });
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const historicData = useMemo(() => {
    let data;
    if (energyData) {
      data = generateHistoricDataFromReal(energyData, scenarioData, gemeinde);
      console.log('[HistoricDevelopmentChart] Real data generated:', {
        hasEnergyData: !!energyData,
        energyDataKeys: Object.keys(energyData),
        sonneCount: energyData.sonne?.length || 0,
        wasserCount: energyData.wasser?.length || 0,
        dataLength: data.length,
        firstPoint: data[0],
        lastPoint: data[data.length - 1],
        sampleDataPoint: data[2] // 2022 data point for inspection
      });
      
      // Enhanced debugging for final historic data
      console.log('[HistoricDevelopmentChart] Final historic data points:', 
        data.map(d => ({
          year: d.year,
          installations: d.installations,
          totalPower: Math.round(d.totalPower * 100) / 100,
          solarInstallations: d.solarInstallations,
          solarPower: Math.round(d.solarPower * 100) / 100,
          waterInstallations: d.waterInstallations,
          waterPower: Math.round(d.waterPower * 100) / 100,
          biomassInstallations: d.biomassInstallations,
          biomassPower: Math.round(d.biomassPower * 100) / 100,
          biogasInstallations: d.biogasInstallations,
          biogasPower: Math.round(d.biogasPower * 100) / 100,
          isProjected: d.isProjected
        }))
      );
    } else {
      data = generateMockHistoricData(scenarioData);
      console.log('[HistoricDevelopmentChart] Mock data generated:', {
        dataLength: data.length,
        firstPoint: data[0],
        lastPoint: data[data.length - 1]
      });
    }
    return data;
  }, [energyData, scenarioData]);
  
  // [CP01] Filter sources that have power values > 0 at some point in time
  const activeSourceTypes = useMemo(() => {
    const types = {
      solar: historicData.some(d => d.solarPower > 0),
      water: historicData.some(d => d.waterPower > 0),
      biomass: historicData.some(d => d.biomassPower > 0),
      biogas: historicData.some(d => d.biogasPower > 0)
    };
    
    console.log('[HistoricDevelopmentChart] Active source types:', {
      activeTypes: types,
      powerValues: {
        maxSolar: Math.max(...historicData.map(d => d.solarPower)),
        maxWater: Math.max(...historicData.map(d => d.waterPower)),
        maxBiomass: Math.max(...historicData.map(d => d.biomassPower)),
        maxBiogas: Math.max(...historicData.map(d => d.biogasPower)),
        maxTotal: Math.max(...historicData.map(d => d.totalPower))
      }
    });
    
    return types;
  }, [historicData]);
  
  // Chart uses the main historicData directly for better reliability
  
  // Calculate trend statistics based on power generation
  const trendStats = useMemo(() => {
    const historicPoints = historicData.filter(d => !d.isProjected);
    if (historicPoints.length < 2) return { powerGrowth: 0, projectedYear: null };
    
    const firstYear = historicPoints[0];
    const lastYear = historicPoints[historicPoints.length - 1];
    
    // Calculate growth based on total power generation
    const powerGrowth = firstYear.totalPower > 0 
      ? ((lastYear.totalPower - firstYear.totalPower) / firstYear.totalPower) * 100
      : 0;
    
    return {
      powerGrowth: Math.round(powerGrowth * 10) / 10,
      projectedYear: historicData[historicData.length - 1]
    };
  }, [historicData]);

  // Enhanced data validation with detailed checks
  const validationResult = useMemo(() => {
    const hasData = historicData.length > 0;
    const hasAnyPower = historicData.some(d => d.totalPower > 0 || d.solarPower > 0 || d.waterPower > 0 || d.biomassPower > 0 || d.biogasPower > 0);
    const hasMinimumDataPoints = historicData.length >= 3; // Need at least 3 years for trend
    const hasValidYears = historicData.every(d => d.year >= 2020 && d.year <= 2025);
    const totalMaxPower = Math.max(...historicData.map(d => d.totalPower));
    const hasActiveTypes = Object.values(activeSourceTypes).some(Boolean);
    
    return {
      hasData,
      hasAnyPower,
      hasMinimumDataPoints,
      hasValidYears,
      totalMaxPower,
      hasActiveTypes,
      isValid: hasData && hasAnyPower && hasMinimumDataPoints && hasValidYears && totalMaxPower > 0
    };
  }, [historicData, activeSourceTypes]);
  
  // Comprehensive debug logging for chart rendering
  console.log('[HistoricDevelopmentChart] Comprehensive validation:', {
    ...validationResult,
    dataLength: historicData.length,
    isMobile,
    sampleYears: historicData.map(d => d.year),
    samplePowerValues: historicData.map(d => ({ year: d.year, totalPower: d.totalPower, solar: d.solarPower, isProjected: d.isProjected })),
    activeSourceTypes,
    energyDataAvailable: !!energyData,
    scenarioDataAvailable: !!scenarioData,
    has2025Data: historicData.some(d => d.year === 2025),
    projectionData: historicData.find(d => d.isProjected)
  });
  
  if (isMobile === null) return null;
  
  if (isMobile) {
    // For mobile, convert historic data to simpler format
    const mobileData = historicData.map(d => ({
      jahr: d.year,
      wasser: d.waterPower,
      sonne: d.solarPower,
      biomasse: d.biomassPower,
      biogas: d.biogasPower,
      gesamt: d.totalPower
    }));
    return <HistoricDevelopmentChartMobile data={mobileData} gemeindeName="" />;
  }
  
  // Show fallback if no valid data with enhanced error messaging
  if (!validationResult.isValid) {
    let errorMessage = "Keine Daten für historische Entwicklung verfügbar";
    let subMessage = "Daten werden geladen...";
    
    if (!validationResult.hasData) {
      errorMessage = "Keine historischen Daten verfügbar";
      subMessage = "Versuchen Sie eine andere Gemeinde";
    } else if (!validationResult.hasAnyPower) {
      errorMessage = "Keine Leistungsdaten verfügbar";
      subMessage = "Diese Gemeinde hat möglicherweise keine registrierten Anlagen";
    } else if (!validationResult.hasMinimumDataPoints) {
      errorMessage = "Unzureichende Datenpunkte für Trend";
      subMessage = `Nur ${historicData.length} von mindestens 3 Jahren verfügbar`;
    }
    
    console.warn('[HistoricDevelopmentChart] Showing fallback due to validation failure:', {
      validationResult,
      errorMessage,
      subMessage
    });
    
    return (
      <div className={className}>
        <div className="h-full flex items-center justify-center text-gray-500 text-sm">
          <div className="text-center">
            <div className="mb-2">📊</div>
            <div>{errorMessage}</div>
            <div className="text-xs mt-1">{subMessage}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
        {/* Chart takes full available height */}
        <div className="h-full flex flex-col">
          {/* Compact header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Entwicklung 2020-2025
              </span>
            </div>
            <div className="text-xs text-gray-500">
              +{trendStats.powerGrowth}% Einspeisung seit 2020
            </div>
          </div>
          
          {/* Chart - takes remaining space */}
          <div className="flex-1 min-h-[250px]">
          {/* Debug wrapper to check if ResponsiveContainer is rendering */}
          <div className="w-full h-full border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800">
            {/* Pre-render debugging */}
            {console.log('[HistoricDevelopmentChart] About to render chart with:', {
              chartType: 'LineChart',
              dataLength: historicData.length,
              firstDataPoint: historicData[0],
              lastDataPoint: historicData[historicData.length - 1],
              linesCount: {
                solar: (activeSourceTypes.solar || historicData.some(d => d.solarInstallations > 0)) ? 1 : 0,
                water: activeSourceTypes.water ? 1 : 0,
                biomass: activeSourceTypes.biomass ? 1 : 0,
                biogas: activeSourceTypes.biogas ? 1 : 0,
                total: 1
              },
              maxPowerValue: Math.max(...historicData.map(d => d.totalPower)),
              containerSize: 'width=100%, height=100%'
            })}
            <ResponsiveContainer 
              width="100%" 
              height="100%" 
              minHeight={200}
              debounceMs={50}
            >
            <LineChart data={historicData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="year" 
                stroke="#6b7280"
                fontSize={12}
              />
               <YAxis 
                yAxisId="power"
                orientation="left"
                stroke="#10b981"
                fontSize={12}
                tickFormatter={(value) => `${Math.round(value).toLocaleString()} kWh`}
                domain={[0, 'dataMax']}
                width={60}
                allowDataOverflow={false}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Reference line for current year */}
              <ReferenceLine 
                yAxisId="power"
                x={2024} 
                stroke="#ef4444" 
                strokeDasharray="2 2" 
              />
              
              {/* Solar power line */}
              {(activeSourceTypes.solar || historicData.some(d => d.solarInstallations > 0)) && (
                <Line
                  key={`solar-power-line-${historicData.length}`}
                  yAxisId="power"
                  type="monotone"
                  dataKey="solarPower"
                  stroke="#eab308"
                  strokeWidth={2}
                  dot={false}
                  name="Solar"
                  connectNulls={true}
                />
              )}
              
              {/* Water power line */}
              {activeSourceTypes.water && (
                <Line
                  key={`water-power-line-${historicData.length}`}
                  yAxisId="power"
                  type="monotone"
                  dataKey="waterPower"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  name="Wasserkraft"
                  connectNulls={true}
                />
              )}
              
              {/* Biomass power line */}
              {activeSourceTypes.biomass && (
                <Line
                  key={`biomass-power-line-${historicData.length}`}
                  yAxisId="power"
                  type="monotone"
                  dataKey="biomassPower"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={false}
                  name="Biomasse"
                  connectNulls={true}
                />
              )}
              
              {/* Biogas power line */}
              {activeSourceTypes.biogas && (
                <Line
                  key={`biogas-power-line-${historicData.length}`}
                  yAxisId="power"
                  type="monotone"
                  dataKey="biogasPower"
                  stroke="#ec4899"
                  strokeWidth={2}
                  dot={false}
                  name="Biogas"
                  connectNulls={true}
                />
              )}
              
              {/* Total power line with proper projection styling */}
              <Line
                key={`total-power-line-${historicData.length}`}
                yAxisId="power"
                type="monotone"
                dataKey="totalPower"
                stroke="#10b981"
                strokeWidth={3}
                dot={(props) => {
                  const { cx, cy, payload } = props as { cx: number; cy: number; payload: HistoricDataPoint };
                  if (!payload || typeof cx !== 'number' || typeof cy !== 'number') return null;
                  return (
                    <circle
                      key={`total-dot-${payload.year}-${cx}-${cy}`}
                      cx={cx}
                      cy={cy}
                      r={payload.isProjected ? 6 : 4}
                      fill={payload.isProjected ? "#f97316" : "#10b981"}
                      stroke={payload.isProjected ? "#ea580c" : "#059669"}
                      strokeWidth={2}
                    />
                  );
                }}
                name="Einspeisung (kWh)"
                connectNulls={true}
              />
            </LineChart>
            </ResponsiveContainer>
          </div>
          </div>
          
          {/* Compact legend with colored text */}
          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-center gap-3 text-xs flex-wrap">
              {activeSourceTypes.solar && (
                <div className="flex items-center gap-1">
                  <Sun className="w-3 h-3" style={{ color: '#eab308' }} />
                  <span style={{ color: '#eab308' }}>Solar</span>
                </div>
              )}
              {activeSourceTypes.water && (
                <div className="flex items-center gap-1">
                  <Droplets className="w-3 h-3" style={{ color: '#3b82f6' }} />
                  <span style={{ color: '#3b82f6' }}>Wasser</span>
                </div>
              )}
              {activeSourceTypes.biomass && (
                <div className="flex items-center gap-1">
                  <Leaf className="w-3 h-3" style={{ color: '#8b5cf6' }} />
                  <span style={{ color: '#8b5cf6' }}>Biomasse</span>
                </div>
              )}
              {activeSourceTypes.biogas && (
                <div className="flex items-center gap-1">
                  <Leaf className="w-3 h-3" style={{ color: '#ec4899' }} />
                  <span style={{ color: '#ec4899' }}>Biogas</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Zap className="w-3 h-3" style={{ color: '#10b981' }} />
                <span style={{ color: '#10b981' }}>Einspeisung</span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3" style={{ color: '#f97316' }} />
                <span style={{ color: '#f97316' }}>Prognose</span>
              </div>
            </div>
          </div>
        </div>
    </motion.div>
  );
};